import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CONTRATOS_KEYS,
  ContractSituacao,
  CreateContractPayload,
  ListContractsParams,
  SignatoryInput,
  UpdateContractPayload,
  adicionarClausula,
  aprovarAditivo,
  assinarComoSignatarioInterno,
  atualizarContrato,
  atualizarModelo,
  atualizarSituacaoContrato,
  atualizarTemplateClausula,
  buscarContrato,
  buscarIndicadores,
  buscarSaldo,
  buscarStatusAssinatura,
  concluirAditivo,
  criarAditivo,
  criarAlertaGlobal,
  criarConfigAlerta,
  criarContrato,
  criarModelo,
  criarTemplateClausula,
  desativarAlertaGlobal,
  desativarConfigAlerta,
  desativarModelo,
  desativarTemplateClausula,
  enviarParaAssinatura,
  listarAditivos,
  listarAlertasGlobais,
  listarAnexos,
  listarClausulas,
  listarConfigAlertas,
  listarContratos,
  listarHistoricoContrato,
  listarModelos,
  listarTemplatesClausulas,
  listarTemplatesClausulasAdmin,
  previewGeracaoContrato,
  registrarAssinaturaManual,
  reenviarConviteAssinatura,
  rejeitarAditivo,
  removerAnexo,
  removerClausula,
  renovarContrato,
  uploadAnexo,
  type AddClausePayload,
  type ClauseTemplatePayload,
  type ContractModelPayload,
  type CreateAddendumPayload,
  type ProposalImportCondition,
  type RenewContractPayload,
} from '../api';

export { CONTRATOS_KEYS };

export function useContratos(params?: ListContractsParams) {
  return useQuery({
    queryKey: CONTRATOS_KEYS.list(params),
    queryFn: () => listarContratos(params),
  });
}

export function useContrato(id: string | undefined) {
  return useQuery({
    queryKey: id ? CONTRATOS_KEYS.detail(id) : ['contratos', 'detail', 'noop'],
    queryFn: () => buscarContrato(id as string),
    enabled: !!id,
  });
}

export function useCreateContrato() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateContractPayload) => criarContrato(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: CONTRATOS_KEYS.all });
    },
  });
}

export function useUpdateContrato() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateContractPayload }) =>
      atualizarContrato(id, payload),
    onSuccess: (_data, { id }) => {
      qc.invalidateQueries({ queryKey: CONTRATOS_KEYS.detail(id) });
      qc.invalidateQueries({ queryKey: CONTRATOS_KEYS.all });
    },
  });
}

export function useAtualizarSituacao() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      situacao,
      reason,
    }: {
      id: string;
      situacao: ContractSituacao;
      reason?: string;
    }) => atualizarSituacaoContrato(id, situacao, reason),
    onSuccess: (_data, { id }) => {
      qc.invalidateQueries({ queryKey: CONTRATOS_KEYS.detail(id) });
      qc.invalidateQueries({ queryKey: CONTRATOS_KEYS.all });
    },
  });
}

export function useStatusAssinatura(id: string | undefined) {
  return useQuery({
    queryKey: id ? [...CONTRATOS_KEYS.detail(id), 'signature'] : ['contratos', 'signature', 'noop'],
    queryFn: () => buscarStatusAssinatura(id as string),
    enabled: !!id,
  });
}

export function useEnviarParaAssinatura() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, signatories }: { id: string; signatories: SignatoryInput[] }) =>
      enviarParaAssinatura(id, signatories),
    onSuccess: (_data, { id }) => {
      qc.invalidateQueries({ queryKey: CONTRATOS_KEYS.detail(id) });
    },
  });
}

export function useRenovarContrato() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: RenewContractPayload }) =>
      renovarContrato(id, payload),
    onSuccess: (_data, { id }) => {
      qc.invalidateQueries({ queryKey: CONTRATOS_KEYS.detail(id) });
      qc.invalidateQueries({ queryKey: CONTRATOS_KEYS.all });
    },
  });
}

export function useAssinarComoSignatarioInterno() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => assinarComoSignatarioInterno(id),
    onSuccess: (_data, id) => {
      qc.invalidateQueries({ queryKey: CONTRATOS_KEYS.detail(id) });
      qc.invalidateQueries({ queryKey: CONTRATOS_KEYS.all });
    },
  });
}

export function useRegistrarAssinaturaManual() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, signatories }: { id: string; signatories: SignatoryInput[] }) =>
      registrarAssinaturaManual(id, signatories),
    onSuccess: (_data, { id }) => {
      qc.invalidateQueries({ queryKey: CONTRATOS_KEYS.detail(id) });
      qc.invalidateQueries({ queryKey: CONTRATOS_KEYS.all });
    },
  });
}

export function useHistoricoContrato(id: string | undefined) {
  return useQuery({
    queryKey: id ? CONTRATOS_KEYS.history(id) : ['contratos', 'history', 'noop'],
    queryFn: () => listarHistoricoContrato(id as string, { limit: 10 }),
    enabled: !!id,
  });
}

export function useSaldoContrato(id: string | undefined) {
  return useQuery({
    queryKey: id ? CONTRATOS_KEYS.balance(id) : ['contratos', 'balance', 'noop'],
    queryFn: () => buscarSaldo(id as string),
    enabled: !!id,
  });
}

/**
 * Pré-visualiza a geração do contrato a partir da proposta (RN-COM-003).
 * `enabled` só liga quando o usuário já escolheu uma proposta.
 */
export function usePreviewGeracaoContrato(
  proposalId: string | undefined,
  clientConditions: ProposalImportCondition[] = [],
) {
  return useQuery({
    queryKey: [
      'contratos',
      'proposta-preview',
      proposalId,
      clientConditions.map((c) => `${c.field}:${c.actual}`).join('|'),
    ],
    queryFn: () => previewGeracaoContrato(proposalId as string, clientConditions),
    enabled: !!proposalId,
    staleTime: 30_000,
  });
}

export function useIndicadoresContratos(params?: {
  clientId?: string;
  unidadeId?: string;
  periodFrom?: string;
  periodTo?: string;
}) {
  return useQuery({
    queryKey: CONTRATOS_KEYS.indicators(params as Record<string, unknown>),
    queryFn: () => buscarIndicadores(params),
  });
}

// ======================================================================
// Cláusulas
// ======================================================================

export function useClausulasContrato(id: string | undefined) {
  return useQuery({
    queryKey: id ? [...CONTRATOS_KEYS.detail(id), 'clauses'] : ['contratos', 'clauses', 'noop'],
    queryFn: () => listarClausulas(id as string),
    enabled: !!id,
  });
}

export function useTemplatesClausulas(id: string | undefined, modality?: string) {
  return useQuery({
    queryKey: id
      ? [...CONTRATOS_KEYS.detail(id), 'clause-templates', modality ?? null]
      : ['contratos', 'clause-templates', 'noop'],
    queryFn: () => listarTemplatesClausulas(id as string, modality),
    enabled: !!id,
  });
}

export function useAdicionarClausula() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: AddClausePayload }) =>
      adicionarClausula(id, payload),
    onSuccess: (_data, { id }) => {
      qc.invalidateQueries({ queryKey: [...CONTRATOS_KEYS.detail(id), 'clauses'] });
    },
  });
}

export function useRemoverClausula() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, clauseId }: { id: string; clauseId: string }) => removerClausula(id, clauseId),
    onSuccess: (_data, { id }) => {
      qc.invalidateQueries({ queryKey: [...CONTRATOS_KEYS.detail(id), 'clauses'] });
    },
  });
}

// ======================================================================
// Documentos/Anexos
// ======================================================================

export function useAnexosContrato(id: string | undefined) {
  return useQuery({
    queryKey: id ? [...CONTRATOS_KEYS.detail(id), 'attachments'] : ['contratos', 'attachments', 'noop'],
    queryFn: () => listarAnexos(id as string),
    enabled: !!id,
  });
}

export function useUploadAnexo() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, documentType, file }: { id: string; documentType: string; file: File }) =>
      uploadAnexo(id, { documentType, file }),
    onSuccess: (_data, { id }) => {
      qc.invalidateQueries({ queryKey: [...CONTRATOS_KEYS.detail(id), 'attachments'] });
    },
  });
}

export function useRemoverAnexo() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, attachmentId }: { id: string; attachmentId: string }) =>
      removerAnexo(id, attachmentId),
    onSuccess: (_data, { id }) => {
      qc.invalidateQueries({ queryKey: [...CONTRATOS_KEYS.detail(id), 'attachments'] });
    },
  });
}

// ======================================================================
// Aditivos
// ======================================================================

export function useAditivos(id: string | undefined) {
  return useQuery({
    queryKey: id ? [...CONTRATOS_KEYS.detail(id), 'addenda'] : ['contratos', 'addenda', 'noop'],
    queryFn: () => listarAditivos(id as string),
    enabled: !!id,
  });
}

export function useCriarAditivo() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: CreateAddendumPayload }) =>
      criarAditivo(id, payload),
    onSuccess: (_data, { id }) => {
      qc.invalidateQueries({ queryKey: [...CONTRATOS_KEYS.detail(id), 'addenda'] });
    },
  });
}

export function useAprovarAditivo() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, addendumId, observacoes }: { id: string; addendumId: string; observacoes?: string }) =>
      aprovarAditivo(id, addendumId, observacoes),
    onSuccess: (_data, { id }) => {
      qc.invalidateQueries({ queryKey: [...CONTRATOS_KEYS.detail(id), 'addenda'] });
      qc.invalidateQueries({ queryKey: CONTRATOS_KEYS.detail(id) });
    },
  });
}

export function useRejeitarAditivo() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, addendumId, justificativa }: { id: string; addendumId: string; justificativa: string }) =>
      rejeitarAditivo(id, addendumId, justificativa),
    onSuccess: (_data, { id }) => {
      qc.invalidateQueries({ queryKey: [...CONTRATOS_KEYS.detail(id), 'addenda'] });
    },
  });
}

export function useConcluirAditivo() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, addendumId }: { id: string; addendumId: string }) => concluirAditivo(id, addendumId),
    onSuccess: (_data, { id }) => {
      qc.invalidateQueries({ queryKey: [...CONTRATOS_KEYS.detail(id), 'addenda'] });
      qc.invalidateQueries({ queryKey: CONTRATOS_KEYS.detail(id) });
    },
  });
}

// ======================================================================
// Modelos de contrato (administração)
// ======================================================================

const MODELOS_KEY = ['contratos', 'modelos'] as const;

export function useModelosContrato(filters?: {
  serviceType?: string;
  clientId?: string;
  unitId?: string;
  isActive?: boolean;
}) {
  return useQuery({
    queryKey: [...MODELOS_KEY, filters ?? {}],
    queryFn: () => listarModelos(filters),
  });
}

export function useCriarModelo() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: ContractModelPayload) => criarModelo(payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: MODELOS_KEY }),
  });
}

export function useAtualizarModelo() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: Partial<ContractModelPayload> & { isActive?: boolean } }) =>
      atualizarModelo(id, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: MODELOS_KEY }),
  });
}

export function useDesativarModelo() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => desativarModelo(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: MODELOS_KEY }),
  });
}

// ======================================================================
// Configuração de alertas por contrato
// ======================================================================

export function useConfigAlertasContrato(id: string | undefined) {
  return useQuery({
    queryKey: id ? [...CONTRATOS_KEYS.detail(id), 'alert-configs'] : ['contratos', 'alert-configs', 'noop'],
    queryFn: () => listarConfigAlertas(id as string),
    enabled: !!id,
  });
}

export function useCriarConfigAlerta() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, daysBefore, alertType }: { id: string; daysBefore: number; alertType?: string }) =>
      criarConfigAlerta(id, { daysBefore, alertType }),
    onSuccess: (_data, { id }) => {
      qc.invalidateQueries({ queryKey: [...CONTRATOS_KEYS.detail(id), 'alert-configs'] });
    },
  });
}

export function useDesativarConfigAlerta() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, configId }: { id: string; configId: string }) => desativarConfigAlerta(id, configId),
    onSuccess: (_data, { id }) => {
      qc.invalidateQueries({ queryKey: [...CONTRATOS_KEYS.detail(id), 'alert-configs'] });
    },
  });
}

// ======================================================================
// Alertas globais (administração)
// ======================================================================

const ALERTAS_GLOBAIS_KEY = ['contratos', 'alertas-globais'] as const;

export function useAlertasGlobais() {
  return useQuery({ queryKey: ALERTAS_GLOBAIS_KEY, queryFn: listarAlertasGlobais });
}

export function useCriarAlertaGlobal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: { daysBefore: number; alertType?: string }) => criarAlertaGlobal(payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ALERTAS_GLOBAIS_KEY }),
  });
}

export function useDesativarAlertaGlobal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (configId: string) => desativarAlertaGlobal(configId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ALERTAS_GLOBAIS_KEY }),
  });
}

// ======================================================================
// Administração do catálogo de templates de cláusula
// ======================================================================

const CLAUSE_TEMPLATES_ADMIN_KEY = ['contratos', 'clause-templates-admin'] as const;

export function useTemplatesClausulasAdmin(filters?: { modality?: string; isActive?: boolean }) {
  return useQuery({
    queryKey: [...CLAUSE_TEMPLATES_ADMIN_KEY, filters ?? {}],
    queryFn: () => listarTemplatesClausulasAdmin(filters),
  });
}

export function useCriarTemplateClausula() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: ClauseTemplatePayload) => criarTemplateClausula(payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: CLAUSE_TEMPLATES_ADMIN_KEY }),
  });
}

export function useAtualizarTemplateClausula() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: Partial<ClauseTemplatePayload> & { isActive?: boolean } }) =>
      atualizarTemplateClausula(id, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: CLAUSE_TEMPLATES_ADMIN_KEY }),
  });
}

export function useDesativarTemplateClausula() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => desativarTemplateClausula(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: CLAUSE_TEMPLATES_ADMIN_KEY }),
  });
}

// ======================================================================
// Reenvio de convite de assinatura
// ======================================================================

export function useReenviarConviteAssinatura() {
  return useMutation({
    mutationFn: ({ id, email }: { id: string; email: string }) => reenviarConviteAssinatura(id, email),
  });
}
